import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  PhotoCamera as PhotoCameraIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiService, {
  type AdminProgram,
  type AdminProgramAssignment,
  type AdminProgramKind,
  type AdminProgramPatch,
} from "../services/api";
import BrandStringListEditor from "../components/BrandStringListEditor";
import { formatChileanDate } from "../utils/dateUtils";
import { getProgramImageUrl, getPodcastImageUrl } from "../utils/imageUtils";

function formatAssignmentDuration(seconds: number | null): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return "";
  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }
  return `${minutes}:${String(secs).padStart(2, "0")}`;
}

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") resolve(reader.result);
      else reject(new Error("No se pudo leer la imagen"));
    };
    reader.onerror = () => reject(reader.error ?? new Error("Read failed"));
    reader.readAsDataURL(file);
  });

export function ProgramKindChip({ kind }: { kind: AdminProgramKind }) {
  const livestream = kind === "livestream";
  return (
    <Chip
      size="small"
      label={livestream ? "Livestream" : "Podcast"}
      sx={{
        height: 22,
        fontWeight: 700,
        bgcolor: livestream ? "warning.main" : "info.main",
        color: livestream ? "warning.contrastText" : "info.contrastText",
      }}
    />
  );
}

export const adminProgramCoverUrl = (
  program: Pick<AdminProgram, "imageS3Key" | "imageUpdatedAt"> | null,
): string => {
  if (!program?.imageS3Key) return "";
  return getProgramImageUrl({
    imageS3Key: program.imageS3Key,
    imageUpdatedAt: program.imageUpdatedAt,
  });
};

type ProgramAnalysisDrawerProps = {
  programId: string | null;
  programSummary: AdminProgram | null;
  onClose: () => void;
};

type FormState = {
  name: string;
  slug: string;
  description: string;
  aliases: string[];
  keywords: string[];
  isActive: boolean;
  hidden: boolean;
};

const emptyForm = (): FormState => ({
  name: "",
  slug: "",
  description: "",
  aliases: [],
  keywords: [],
  isActive: true,
  hidden: false,
});

const formFromProgram = (program: AdminProgram): FormState => ({
  name: program.name ?? "",
  slug: program.slug ?? "",
  description: program.description ?? "",
  aliases: program.aliases ?? [],
  keywords: program.keywords ?? [],
  isActive: program.isActive !== false,
  hidden: program.hidden === true,
});

const ProgramAnalysisDrawer: React.FC<ProgramAnalysisDrawerProps> = ({
  programId,
  programSummary,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [imageUrl, setImageUrl] = useState("");
  const [assignmentQuery, setAssignmentQuery] = useState("");
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const open = Boolean(programId);

  const detailQuery = useQuery({
    queryKey: ["admin-program", programId],
    queryFn: () => apiService.getAdminProgram(programId!),
    enabled: open,
  });

  const program = detailQuery.data?.program ?? programSummary;

  const assignmentsQuery = useQuery({
    queryKey: ["admin-program-assignments", programId],
    queryFn: () => apiService.getAdminProgramAssignments(programId!),
    enabled: open,
  });

  const assignmentItems = assignmentsQuery.data?.items ?? [];
  const filteredAssignments = useMemo(() => {
    const needle = assignmentQuery.trim().toLowerCase();
    if (!needle) return assignmentItems;
    return assignmentItems.filter((item) => item.title.toLowerCase().includes(needle));
  }, [assignmentItems, assignmentQuery]);

  useEffect(() => {
    setAssignmentQuery("");
  }, [programId]);

  useEffect(() => {
    if (!open) {
      setForm(emptyForm());
      setImageUrl("");
      return;
    }
    if (program) setForm(formFromProgram(program));
  }, [open, program]);

  const saveMutation = useMutation({
    mutationFn: (payload: AdminProgramPatch) =>
      apiService.patchAdminProgram(programId!, payload),
    onSuccess: (result) => {
      queryClient.setQueryData(["admin-program", programId], result);
      void queryClient.invalidateQueries({ queryKey: ["admin-programs"] });
      void queryClient.invalidateQueries({ queryKey: ["admin-channel-programs"] });
      setForm(formFromProgram(result.program));
      setSnackbar({
        open: true,
        message: "Programa actualizado",
        severity: "success",
      });
    },
    onError: (error: unknown) => {
      const apiMessage =
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response &&
        error.response.data &&
        typeof error.response.data === "object" &&
        "error" in error.response.data &&
        typeof (error.response.data as { error?: unknown }).error === "string"
          ? (error.response.data as { error: string }).error
          : "No se pudo guardar el programa";
      setSnackbar({
        open: true,
        message: apiMessage,
        severity: "error",
      });
    },
  });

  const applyImageResult = (result: { program: AdminProgram }, message: string) => {
    queryClient.setQueryData(["admin-program", programId], result);
    void queryClient.invalidateQueries({ queryKey: ["admin-programs"] });
    void queryClient.invalidateQueries({ queryKey: ["admin-channel-programs"] });
    setSnackbar({ open: true, message, severity: "success" });
  };

  const imageMutation = useMutation({
    mutationFn: async (file: File) => {
      const imageBase64 = await fileToBase64(file);
      return apiService.uploadAdminProgramImage(programId!, {
        imageBase64,
        contentType: file.type || "image/png",
        fileName: file.name,
      });
    },
    onSuccess: (result) => applyImageResult(result, "Imagen actualizada"),
    onError: () => {
      setSnackbar({
        open: true,
        message: "No se pudo subir la imagen",
        severity: "error",
      });
    },
  });

  const imageUrlMutation = useMutation({
    mutationFn: (url: string) =>
      apiService.uploadAdminProgramImage(programId!, { imageUrl: url }),
    onSuccess: (result) => {
      applyImageResult(result, "Imagen importada");
      setImageUrl("");
    },
    onError: (error: unknown) => {
      const apiMessage =
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response &&
        error.response.data &&
        typeof error.response.data === "object" &&
        "error" in error.response.data &&
        typeof (error.response.data as { error?: unknown }).error === "string"
          ? (error.response.data as { error: string }).error
          : "No se pudo importar la imagen";
      setSnackbar({
        open: true,
        message: apiMessage,
        severity: "error",
      });
    },
  });

  const handleSave = () => {
    if (!form.name.trim()) {
      setSnackbar({
        open: true,
        message: "El nombre es obligatorio",
        severity: "error",
      });
      return;
    }
    const shared = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      keywords: form.keywords,
      isActive: form.isActive,
      hidden: form.hidden,
    };
    saveMutation.mutate(
      program?.kind === "livestream"
        ? shared
        : {
            ...shared,
            slug: form.slug.trim() || undefined,
            aliases: form.aliases,
          },
    );
  };

  const handleImportFromUrl = () => {
    const url = imageUrl.trim();
    if (!url) {
      setSnackbar({
        open: true,
        message: "Pega una URL de imagen",
        severity: "error",
      });
      return;
    }
    imageUrlMutation.mutate(url);
  };

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setSnackbar({
        open: true,
        message: "El archivo debe ser una imagen",
        severity: "error",
      });
      return;
    }
    imageMutation.mutate(file);
  };

  const coverUrl = adminProgramCoverUrl(program);
  const channelImageUrl = program?.podcast
    ? getPodcastImageUrl(program.podcast)
    : "";
  const isLivestream = (assignmentsQuery.data?.kind ?? program?.kind) === "livestream";
  const assignmentNoun = isLivestream ? "streams" : "contenidos";
  const assignmentTotal = assignmentsQuery.data?.total ?? 0;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 560 },
            bgcolor: "background.default",
          },
        }}
      >
        <Box
          sx={{
            bgcolor: "background.paper",
            p: 2.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" flex={1}>
            <Box sx={{ position: "relative" }}>
              <Avatar
                src={coverUrl || channelImageUrl}
                alt={program?.name ?? ""}
                sx={{ width: 56, height: 56 }}
              >
                {program?.name?.charAt(0) ?? "?"}
              </Avatar>
              <IconButton
                size="small"
                onClick={() => fileInputRef.current?.click()}
                disabled={imageMutation.isPending || imageUrlMutation.isPending}
                sx={{
                  position: "absolute",
                  right: -6,
                  bottom: -6,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  "&:hover": { bgcolor: "background.paper" },
                }}
              >
                {imageMutation.isPending || imageUrlMutation.isPending ? (
                  <CircularProgress size={16} />
                ) : (
                  <PhotoCameraIcon fontSize="small" />
                )}
              </IconButton>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                onChange={(e) => {
                  handleFile(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </Box>
            <Box minWidth={0}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" fontWeight={600} noWrap>
                  {program?.name ?? "Programa"}
                </Typography>
                {program?.kind && <ProgramKindChip kind={program.kind} />}
              </Stack>
              {program?.podcast?.title && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {program.podcast.title}
                </Typography>
              )}
            </Box>
          </Stack>
          <IconButton onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 2.5, overflow: "auto" }}>
          {detailQuery.isLoading && !program ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : detailQuery.isError && !program ? (
            <Alert severity="error">No se pudo cargar el programa.</Alert>
          ) : (
            <Stack spacing={2.5}>
              <Stack
                direction="row"
                alignItems="baseline"
                justifyContent="space-between"
                spacing={1}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  {isLivestream ? "Streams asignados" : "Contenido asignado"}
                </Typography>
                {!assignmentsQuery.isLoading && !assignmentsQuery.isError && (
                  <Typography variant="caption" color="text.secondary">
                    {assignmentTotal}
                    {assignmentTotal > assignmentItems.length
                      ? ` · mostrando ${assignmentItems.length}`
                      : ""}
                  </Typography>
                )}
              </Stack>
              {assignmentsQuery.isLoading ? (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={22} />
                </Box>
              ) : assignmentsQuery.isError ? (
                <Alert severity="error">
                  No se pudieron cargar los {assignmentNoun} de este programa.
                </Alert>
              ) : assignmentTotal === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No hay {assignmentNoun} asignados a este programa.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {assignmentItems.length > 8 && (
                    <TextField
                      size="small"
                      placeholder="Buscar por título…"
                      value={assignmentQuery}
                      onChange={(e) => setAssignmentQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      maxHeight: 360,
                      overflow: "auto",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    {filteredAssignments.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
                        Ningún título coincide.
                      </Typography>
                    ) : (
                      <Stack divider={<Divider flexItem />}>
                        {filteredAssignments.map((item: AdminProgramAssignment) => {
                          const duration = formatAssignmentDuration(item.durationSeconds);
                          const meta = [
                            item.publishedAt ? formatChileanDate(item.publishedAt) : null,
                            duration || null,
                            item.label || null,
                          ]
                            .filter(Boolean)
                            .join(" · ");
                          return (
                            <Stack
                              key={item._id}
                              direction="row"
                              spacing={1}
                              alignItems="flex-start"
                              sx={{ px: 1.5, py: 1 }}
                            >
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ wordBreak: "break-word" }}
                                >
                                  {item.title || "Sin título"}
                                </Typography>
                                {meta && (
                                  <Typography variant="caption" color="text.secondary">
                                    {meta}
                                  </Typography>
                                )}
                              </Box>
                              {item.url && (
                                <Link
                                  href={item.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label="Abrir"
                                  sx={{ display: "inline-flex", mt: 0.25 }}
                                >
                                  <OpenInNewIcon fontSize="small" />
                                </Link>
                              )}
                            </Stack>
                          );
                        })}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              )}
              <Divider />
              <Typography variant="subtitle2" color="text.secondary">
                Imagen
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Se guarda como PNG 800×800. Si no hay portada, se muestra la
                imagen del canal.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                  label="URL de imagen"
                  size="small"
                  fullWidth
                  placeholder="https://…"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleImportFromUrl();
                    }
                  }}
                  disabled={imageUrlMutation.isPending}
                />
                <Button
                  variant="outlined"
                  onClick={handleImportFromUrl}
                  disabled={imageUrlMutation.isPending || !imageUrl.trim()}
                  sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                >
                  {imageUrlMutation.isPending ? "Importando…" : "Importar URL"}
                </Button>
              </Stack>
              <Divider />
              <Typography variant="subtitle2" color="text.secondary">
                Identidad
              </Typography>
              <TextField
                label="Nombre"
                size="small"
                fullWidth
                value={form.name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
              />
              {!isLivestream && (
                <TextField
                  label="Slug"
                  size="small"
                  fullWidth
                  value={form.slug}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, slug: e.target.value }))
                  }
                />
              )}
              <TextField
                label="Descripción"
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={form.description}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, description: e.target.value }))
                }
              />
              {!isLivestream && (
                <BrandStringListEditor
                  items={form.aliases}
                  onChange={(aliases) => setForm((prev) => ({ ...prev, aliases }))}
                  addLabel="Alias"
                  emptyMessage="Sin aliases"
                  listLabel="Aliases"
                />
              )}
              <BrandStringListEditor
                items={form.keywords}
                onChange={(keywords) =>
                  setForm((prev) => ({ ...prev, keywords }))
                }
                addLabel="Keyword"
                emptyMessage="Sin keywords"
                listLabel="Keywords"
              />

              <Divider />
              <Typography variant="subtitle2" color="text.secondary">
                Estado
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isActive}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          isActive: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Activo"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.hidden}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          hidden: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Oculto"
                />
              </Stack>

              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? "Guardando…" : "Guardar cambios"}
              </Button>
            </Stack>
          )}
        </Box>
      </Drawer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProgramAnalysisDrawer;
